import {
	CustomTypeEnum,
	type ResourceSchema,
	type ResourceSchemaColumn
} from '@semanticedge/persistence-service-noheaders-client/src'
import type { ReferenceTableRow } from '@repo/commons/models'
import { getPrimaryKeyColumns } from './resourceSchemaColumns.js'

export type ColumnConflicts = { empty: number; duplicate: number }

const isBlank = (value: unknown): boolean => value == null || value === ''

export const countColumnConflicts = (
	rows: readonly ReferenceTableRow[],
	columnName: string
): ColumnConflicts => {
	const seen = new Set<string>()
	const conflicts: ColumnConflicts = { empty: 0, duplicate: 0 }

	rows.forEach((row) => {
		const value = row[columnName]

		if (isBlank(value)) {
			conflicts.empty += 1

			return
		}

		const key = String(value)
		if (seen.has(key)) conflicts.duplicate += 1
		seen.add(key)
	})

	return conflicts
}

type ColumnValueRule = {
	isValid: (value: unknown) => boolean
	message: string
}

const INTEGER_PATTERN = /^-?\d+$/
const NUMBER_PATTERN = /^-?\d+(?:\.\d+)?$/

const matchesPattern = (value: unknown, pattern: RegExp): boolean =>
	typeof value === 'string' && pattern.test(value.trim())

const COLUMN_VALUE_RULE: Partial<Record<CustomTypeEnum, ColumnValueRule>> = {
	[CustomTypeEnum.Boolean]: {
		isValid: (value) => typeof value === 'boolean' || value === 'true' || value === 'false',
		message: 'Must be true or false'
	},
	[CustomTypeEnum.Integer]: {
		isValid: (value) =>
			(typeof value === 'number' && Number.isInteger(value)) ||
			matchesPattern(value, INTEGER_PATTERN),
		message: 'Must be a valid integer'
	},
	[CustomTypeEnum.Float]: {
		isValid: (value) =>
			(typeof value === 'number' && !Number.isNaN(value)) || matchesPattern(value, NUMBER_PATTERN),
		message: 'Must be a valid number'
	},
	[CustomTypeEnum.String]: {
		isValid: (value) => ['string', 'number', 'boolean'].includes(typeof value),
		message: 'Must be a valid string value'
	}
}

const getColumnValueError = (
	columnType: CustomTypeEnum | undefined,
	value: unknown
): string | null => {
	const rule = columnType ? COLUMN_VALUE_RULE[columnType] : undefined
	if (!rule || isBlank(value) || rule.isValid(value)) return null

	return rule.message
}

export const columnTypeValidator =
	(columnType: CustomTypeEnum | undefined) =>
	(value: unknown, rowIndex: number): string | null => {
		const error = getColumnValueError(columnType, value)

		return error ? `Row ${rowIndex + 1}: ${error}` : null
	}

export const validateReferenceTableRow = (
	row: ReferenceTableRow,
	schema: ResourceSchema,
	existingRows: readonly ReferenceTableRow[],
	editingIndex: number | null = null
): Record<string, string> => {
	const errors: Record<string, string> = {}
	const columns: Record<string, ResourceSchemaColumn> = schema.columns ?? {}
	const primaryKeyColumns = getPrimaryKeyColumns(columns)

	Object.entries(columns).forEach(([name, column]) => {
		const value = row[name]

		if (isBlank(value)) {
			if (column.nullable === false || primaryKeyColumns.includes(name)) errors[name] = 'Required'

			return
		}

		const typeError = getColumnValueError(column.type, value)
		if (typeError) errors[name] = typeError
	})

	primaryKeyColumns.forEach((name) => {
		if (errors[name] || isBlank(row[name])) return

		const isTaken = existingRows.some(
			(existingRow, index) =>
				index !== editingIndex && String(existingRow[name]) === String(row[name])
		)

		if (isTaken) errors[name] = 'Duplicate primary key'
	})

	return errors
}
